import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check } from "lucide-react";

export const AiDescriptionModal = ({ open, onOpenChange, seed = {}, onApply }) => {
  const [fields, setFields] = useState({
    title: seed.title || "", category: seed.category || "", species: seed.species || "",
    breed: seed.breed || "", age: seed.age || "", temperament: "", extra: "", tone: "Friendly",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/generate-description", fields);
      setResult(data.description);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#12234A] flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg bg-[#FFF4F0] flex items-center justify-center"><Sparkles size={18} className="text-[#F5551E]" /></span>
            AI Description Generator
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">Tell us a few details and we'll craft a compelling listing description.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Breed / type</Label>
            <Input value={fields.breed} onChange={(e) => set("breed", e.target.value)} data-testid="ai-breed" className="mt-1 h-10 rounded-xl" placeholder="e.g. Golden Retriever" />
          </div>
          <div>
            <Label>Age</Label>
            <Input value={fields.age} onChange={(e) => set("age", e.target.value)} data-testid="ai-age" className="mt-1 h-10 rounded-xl" placeholder="8 weeks" />
          </div>
          <div>
            <Label>Tone</Label>
            <Select value={fields.tone} onValueChange={(v) => set("tone", v)}>
              <SelectTrigger data-testid="ai-tone" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Friendly">Friendly</SelectItem>
                <SelectItem value="Professional">Professional</SelectItem>
                <SelectItem value="Concise">Concise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Key traits / details</Label>
            <Textarea value={fields.temperament} onChange={(e) => set("temperament", e.target.value)} data-testid="ai-traits" className="mt-1 rounded-xl" placeholder="Vaccinated, friendly with kids, house-trained…" rows={2} />
          </div>
        </div>

        <Button onClick={generate} disabled={loading} data-testid="ai-generate-btn" className="w-full h-11 rounded-xl bg-[#12234A] hover:bg-[#0E1B38] text-white font-semibold gap-2">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} className="text-[#F5551E]" />}
          {result ? "Regenerate" : "Generate description"}
        </Button>

        {result && (
          <div className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-4">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="ai-result">{result}</p>
            <Button onClick={() => { onApply(result); onOpenChange(false); toast.success("Description added!"); }} data-testid="ai-apply-btn" className="mt-3 w-full h-10 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
              <Check size={16} /> Use this description
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
